import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ViewEncapsulation } from '@angular/core';
import { toNumber } from '@ngx-simple/core/coercion';
import { SimInputDirective } from './input.directive';

@Component({
  selector: 'sim-input-number',
  template: `
    <ng-content select="input[simInput]"></ng-content>
    <div class="sim-input-number-handler-wrap">
      <span
        (click)="increment($event)"
        [class.sim-input-number-handler-up-disabled]="disabledUp"
        class="sim-input-number-handler sim-input-number-handler-up"
      >
        +
      </span>
      <span
        (click)="decrement($event)"
        [class.sim-input-number-handler-down-disabled]="disabledDown"
        class="sim-input-number-handler sim-input-number-handler-down"
      >
        -
      </span>
    </div>
  `,
  styles: [
    `
      .sim-input-number {
        position: relative;
        display: block;
      }
      .sim-input-number-handler-wrap {
        position: absolute;
        top: 0;
        right: 0;
        width: 22px;
        height: 100%;
        background: #fff;
        border-left: 1px solid #d9d9d9;
        border-radius: 0 2px 2px 0;
        opacity: 1;
        transition: opacity 0.24s linear 0.1s;
      }
      .sim-input-number-handler {
        position: relative;
        display: flex;
        width: 100%;
        height: 50%;
        overflow: hidden;
        color: rgba(0, 0, 0, 0.45);
        font-weight: 700;
        line-height: 0;
        text-align: center;
        transition: all 0.1s linear;
        justify-content: center;
        align-items: center;
      }
      .sim-input-number-handler-up {
        border-top-right-radius: 2px;
        cursor: pointer;
      }
      .sim-input-number-handler-up:hover {
        height: 60% !important;
      }
      .sim-input-number-handler-down {
        top: 0;
        border-top: 1px solid #d9d9d9;
        border-bottom-right-radius: 2px;
        cursor: pointer;
      }
      .sim-input-number-handler-down:hover {
        height: 60% !important;
      }
      .sim-input-number-handler-up-disabled,
      .sim-input-number-handler-down-disabled {
        cursor: not-allowed;
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-input-number'
  }
})
export class SimInputNumberComponent implements AfterContentInit {
  /** simInput实例 */
  @ContentChild(SimInputDirective) _input: SimInputDirective;

  disabledUp: boolean;
  disabledDown: boolean;

  /** 获取初始值 */
  private parsedValue: string;
  private _nativeElement: HTMLInputElement;
  private _max: number;
  private _min: number;

  constructor(public _changeDetectorRef: ChangeDetectorRef) {}

  ngAfterContentInit() {
    console.log(this._input);
    if (!this._input) {
      throw Error('sim-form-field must contain a simInput');
    }
    this.parsedValue = this._input.value;
    this._nativeElement = this._input._elementRef.nativeElement as HTMLInputElement;
  }

  /** 递增值 */
  increment(event: MouseEvent) {
    this.setStep('increment', event);
  }

  /** 递减值 */
  decrement(event: MouseEvent) {
    this.setStep('decrement', event);
  }

  private setValue(value: number) {
    if (`${this.parsedValue}` !== `${value}`) {
      this._input.value = `${value}`;
    }
    this.parsedValue = `${value}`;
    this.disabledUp = this.disabledDown = false;
    if (value || value === 0) {
      const val = Number(value);
      console.log(val, this._max);
      if (val >= this._max) {
        this.disabledUp = true;
      }
      if (val <= this._min) {
        this.disabledDown = true;
      }
    }
  }

  private updateDisplayValue(value: number) {
    this._nativeElement.value = `${value}`;
  }

  /** 获取当前值 */
  private getCurrentValidValue(value: string | number): number {
    return toNumber(value, 0);
  }

  private setStep(type: 'increment' | 'decrement', event: MouseEvent) {
    event.stopPropagation();
    if (!this._input.focused) {
      this._input.focus();
    }
    let val = 0;
    const value = this.getCurrentValidValue(this.parsedValue);
    const step = this.getStep();
    if (type === 'increment') {
      val = value + step;
    } else if (type === 'decrement') {
      val = value - step;
    }
    this._max = this.getMax();
    this._min = this.getMin();
    // 不能溢出
    if (val > this._max) {
      val = this._max;
    } else if (val < this._min) {
      val = this._min;
    }
    this.setValue(val);
    this.updateDisplayValue(val);
  }

  /**
   * 获取 <input type="number" /> 的 step属性
   */
  private getStep(): number {
    return toNumber(this._nativeElement.step, 1);
  }
  /**
   * 获取 <input type="number" /> 的 max属性
   */
  private getMax(): number {
    return toNumber(this._nativeElement.max, Infinity);
  }
  /**
   * 获取 <input type="number" /> 的 min 属性
   */
  private getMin(): number {
    return toNumber(this._nativeElement.min, -Infinity);
  }

  private getPrecision(value: number): number {
    const valueString = value.toString();
    if (valueString.indexOf('e-') >= 0) {
      return parseInt(valueString.slice(valueString.indexOf('e-') + 2), 10);
    }
    let precision = 0;
    if (valueString.indexOf('.') >= 0) {
      precision = valueString.length - valueString.indexOf('.') - 1;
    }
    return precision;
  }
}
